import React, { useState, useEffect } from 'react';
import { useUserDetialsContext } from '../context/user-details';
import PageContainer from '../components/UI/PageContainer';

// Helper component for chunk visualization tabs
const ChunkViewer = ({ chunk }) => {
    const [activeTab, setActiveTab] = useState('output');

    // Force 'trace' tab if failed
    React.useEffect(() => {
        if (chunk.status === 'FAILED' && chunk.error_log) {
            setActiveTab('trace');
        }
    }, [chunk.status, chunk.error_log]);

    return (
        <div className="flex flex-col h-full w-full">
            <div className="flex border-b border-gray-800 bg-gray-900 px-2 overflow-x-auto">
                <button
                    onClick={() => setActiveTab('output')}
                    className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${activeTab === 'output' ? 'border-purple-500 text-purple-400' : 'border-transparent text-gray-500 hover:text-gray-300 hover:border-gray-600'}`}
                >
                    Generated Output
                </button>
                <button
                    onClick={() => setActiveTab('input')}
                    className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${activeTab === 'input' ? 'border-blue-500 text-blue-400' : 'border-transparent text-gray-500 hover:text-gray-300 hover:border-gray-600'}`}
                >
                    Prompt Input View
                </button>
                {chunk.error_log && (
                    <button
                        onClick={() => setActiveTab('trace')}
                        className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${activeTab === 'trace' ? 'border-red-500 text-red-400' : 'border-transparent text-gray-500 hover:text-gray-300 hover:border-gray-600'}`}
                    >
                        Error Trace
                    </button>
                )}
            </div>
            
            <div className="p-4 bg-gray-950 overflow-y-auto max-h-[500px]">
                {activeTab === 'output' && (
                    <div className="whitespace-pre-wrap font-sans text-sm text-gray-300 leading-relaxed">
                        {chunk.text_result ? chunk.text_result : <span className="text-gray-600 italic font-mono text-xs">Waiting for model to generate completion...</span>}
                    </div>
                )}
                
                {activeTab === 'input' && (
                    <div className="whitespace-pre-wrap font-mono text-xs text-gray-400 leading-normal">
                        {chunk.prompt_input ? chunk.prompt_input : <span className="text-gray-600 italic">No prompt data available yet.</span>}
                    </div>
                )}
                
                {activeTab === 'trace' && chunk.error_log && (
                    <div className="whitespace-pre-wrap font-mono text-xs text-red-400 bg-red-950/30 p-4 rounded border border-red-900/50">
                        {chunk.error_log}
                    </div>
                )}
            </div>
        </div>
    );
};

const LabV3 = () => {
    const { userDetails } = useUserDetialsContext();
    const authorizedEmails = ['wyattelliott27@gmail.com', 'michael@fullfr.me'];

    const [file, setFile] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [extractedText, setExtractedText] = useState('');
    const [error, setError] = useState(null);

    const [summary, setSummary] = useState('');
    const [summary2, setSummary2] = useState('');
    const [isSummarizing, setIsSummarizing] = useState(false);
    const [summaryError, setSummaryError] = useState(null);

    const [jobId, setJobId] = useState(null);
    const [jobStatus, setJobStatus] = useState(null);
    const [chunks, setChunks] = useState([]);
    const [totalChunks, setTotalChunks] = useState(0);
    const [finalSynopsis, setFinalSynopsis] = useState(null);
    const [finalMeta, setFinalMeta] = useState(null); // { tokens, cost, model, attempts, attempt_log }
    const [extensionEnabled, setExtensionEnabled] = useState(true);

    // Short synopsis
    const [shortSynopsis, setShortSynopsis] = useState(null);

    // Meta distillation pass results
    const [metaLogline, setMetaLogline] = useState(null);
    const [metaAdaptability, setMetaAdaptability] = useState(null);
    const [metaDemographics, setMetaDemographics] = useState(null);
    const [metaComps, setMetaComps] = useState(null);

    // Character extraction pass results
    const [metaCharacters, setMetaCharacters] = useState([]);

    // Best-effort note is parsed from final_synopsis when it starts with "Best-effort:"
    const finalSynopsisNote = React.useMemo(() => {
        if (!finalSynopsis || !finalSynopsis.startsWith('Best-effort:')) return null;
        const idx = finalSynopsis.indexOf('\n\n');
        return idx > 0 ? finalSynopsis.slice(0, idx).trim() : finalSynopsis.trim();
    }, [finalSynopsis]);
    const finalSynopsisBody = React.useMemo(() => {
        if (!finalSynopsis || !finalSynopsis.startsWith('Best-effort:')) return finalSynopsis;
        const idx = finalSynopsis.indexOf('\n\n');
        return idx > 0 ? finalSynopsis.slice(idx + 2).trim() : '';
    }, [finalSynopsis]);

    const allChunksTerminal = chunks.length > 0 && chunks.every(c => (c.status === 'COMPLETED' || c.status === 'FAILED'));
    const finalSynopsisStatus = (() => {
        // If the job failed, treat the final synopsis as failed unless we already have it.
        if (finalSynopsis && finalSynopsis.trim().length > 0) return 'COMPLETED';
        if (!jobId) return null;
        if (jobStatus === 'FAILED') return 'FAILED';
        // If chunks are still being produced, final synopsis hasn't started yet.
        if (!allChunksTerminal) return 'PENDING';
        // Chunks are done, job still processing => final synopsis in progress.
        if (jobStatus === 'PENDING' || jobStatus === 'PROCESSING') return 'PROCESSING';
        // Edge case: job completed but synopsis missing.
        if (jobStatus === 'COMPLETED') return 'FAILED';
        return 'PENDING';
    })();

    const handleFileChange = (e) => {
        setFile(e.target.files[0]);
        setExtractedText('');
        setError(null);
        setJobId(null);
        setJobStatus(null);
        setChunks([]);
        setTotalChunks(0);
        setFinalSynopsis(null);
        setSummaryError(null);
        setShortSynopsis(null);
        setMetaLogline(null);
        setMetaAdaptability(null);
        setMetaDemographics(null);
        setMetaComps(null);
        setMetaCharacters([]);
    };

    const handleUpload = async () => {
        if (!file) return;

        const formData = new FormData();
        formData.append('file', file);

        setIsLoading(true);
        setError(null);

        try {
            const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}v3/extract/`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${userDetails?.tokenId}`
                },
                body: formData,
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.error || 'Upload failed');
            }

            setExtractedText(data.text);
            
            // Automatically trigger summarize after successful extraction
            await handleSummarize(data.text);
            
        } catch (e) {
            console.error("Pipeline Error:", e);
            setError(e.message);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSummarize = async (textToSummarize) => {
        if (!textToSummarize) return;

        setIsSummarizing(true);
        setSummaryError(null);
        setJobId(null);
        setJobStatus('PENDING');
        setChunks([]);
        setFinalSynopsis(null);
        setShortSynopsis(null);
        setMetaLogline(null);
        setMetaAdaptability(null);
        setMetaDemographics(null);
        setMetaComps(null);
        setMetaCharacters([]);

        try {
            const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}v3/summarize/`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${userDetails?.tokenId}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ text: textToSummarize }),
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.error || 'Summarization failed');
            }

            setJobId(data.job_id);
            setTotalChunks(data.total_chunks);
        } catch (e) {
            console.error("Summarize Error:", e);
            setSummaryError(e.message);
            setIsSummarizing(false);
        }
    };

    const fetchJobStatus = React.useCallback(async () => {
        if (!jobId || !userDetails?.tokenId) return;
        try {
            const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}v3/summarize/status/${jobId}/`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${userDetails?.tokenId}`
                }
            });
            const data = await response.json();

            if (response.ok) {
                setJobStatus(data.job_status);
                setChunks(data.chunks || []);
                if (data.final_synopsis) {
                    setFinalSynopsis(data.final_synopsis);
                }
                if (data.final_model || data.final_tokens || data.final_cost) {
                    setFinalMeta({
                        tokens: data.final_tokens || 0,
                        cost: data.final_cost || 0.0,
                        model: data.final_model || null,
                        attempts: data.final_attempts || 0,
                        attempt_log: data.final_attempt_log || [],
                    });
                }
                if (data.short_synopsis) setShortSynopsis(data.short_synopsis);
                if (data.meta_logline) setMetaLogline(data.meta_logline);
                if (data.meta_adaptability) setMetaAdaptability(data.meta_adaptability);
                if (data.meta_demographics && Object.keys(data.meta_demographics).length > 0) setMetaDemographics(data.meta_demographics);
                if (data.meta_comps && Object.keys(data.meta_comps).length > 0) setMetaComps(data.meta_comps);
                if (data.meta_characters && data.meta_characters.length > 0) setMetaCharacters(data.meta_characters);
                if (data.job_status === 'COMPLETED' || data.job_status === 'FAILED') {
                    setIsSummarizing(false);
                    if (data.job_status === 'FAILED') {
                        setSummaryError(data.error_message || 'Background processing failed.');
                    }
                    return true; // signal to stop polling
                }
            }
        } catch (e) {
            console.error("Polling error:", e);
        }
        return false;
    }, [jobId, userDetails?.tokenId]);

    React.useEffect(() => {
        let intervalId;
        if (jobId && (jobStatus === 'PENDING' || jobStatus === 'PROCESSING')) {
            const poll = async () => {
                const done = await fetchJobStatus();
                if (done && intervalId) clearInterval(intervalId);
            };
            poll(); // immediate first poll
            intervalId = setInterval(poll, 3000);
        }
        return () => {
            if (intervalId) clearInterval(intervalId);
        };
    }, [jobId, jobStatus, userDetails, fetchJobStatus]);

    const handleLongSynopsisTest = async () => {
        setIsSummarizing(true);
        setSummaryError(null);
        setJobId(null);
        setJobStatus('PENDING');
        setChunks([]);
        setFinalSynopsis(null);
        setShortSynopsis(null);
        setMetaLogline(null);
        setMetaAdaptability(null);
        setMetaDemographics(null);
        setMetaComps(null);
        setMetaCharacters([]);
        setExtractedText('[Long Synopsis Test — pre-filled BOLLYWOOD chunks]');

        try {
            const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}v3/test-synopsis/`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${userDetails?.tokenId}`,
                    'Content-Type': 'application/json'
                },
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.error || 'Test synopsis request failed');
            }

            setJobId(data.job_id);
            setTotalChunks(data.total_chunks);
        } catch (e) {
            console.error("Long Synopsis Test Error:", e);
            setSummaryError(e.message);
            setIsSummarizing(false);
        }
    };

    const handleCopyAll = () => {
        let text = "--- OBSERVABILITY LOGS ---\n\n";
        chunks.forEach((c, i) => {
            text += `Chunk ${i + 1} [${c.status}]:\n`;
            text += `Tokens: ${c.tokens || 0} | Cost: $${(c.cost || 0).toFixed(4)}\n\n`;
            text += `Prompt Input:\n${c.prompt_input || 'N/A'}\n\n`;
            text += `Generated Output:\n${c.text_result || 'N/A'}\n\n`;
            if (c.error_log) text += `Error Trace:\n${c.error_log}\n\n`;
            text += '------------------------\n\n';
        });
        if (finalSynopsisStatus) {
            text += `Final Synopsis [${finalSynopsisStatus}]:\n`;
            if (jobStatus === 'FAILED' && summaryError) {
                text += `Error: ${summaryError}\n\n`;
            } else {
                text += `\n`;
            }
            text += '------------------------\n\n';
        }
        if (finalSynopsis) {
            text += `--- FINAL SYNOPSIS ---\n\n${finalSynopsis}\n`;
        }
        navigator.clipboard.writeText(text);
        alert("Copied all logs and synopsis!");
    };

    const handleCopyChunks = () => {
        let text = "--- OBSERVABILITY LOGS ---\n\n";
        chunks.forEach((c, i) => {
            text += `Chunk ${i + 1} [${c.status}]:\n`;
            text += `Tokens: ${c.tokens || 0} | Cost: $${(c.cost || 0).toFixed(4)}\n\n`;
            text += `Prompt Input:\n${c.prompt_input || 'N/A'}\n\n`;
            text += `Generated Output:\n${c.text_result || 'N/A'}\n\n`;
            if (c.error_log) text += `Error Trace:\n${c.error_log}\n\n`;
            text += '------------------------\n\n';
        });
        navigator.clipboard.writeText(text);
        alert("Copied all chunks!");
    };

    const handleCopySynopsis = () => {
        if (!finalSynopsis) return;
        navigator.clipboard.writeText(finalSynopsis);
        alert("Copied final synopsis!");
    };

    // Loading State
    if (!userDetails || !userDetails.email) {
        return (
            <PageContainer>
                <div className="flex flex-col items-center justify-center h-[50vh] text-white">
                    <h1 className="text-2xl font-bold mb-4">Verifying Access...</h1>
                    <p>Current Email: {userDetails?.email || 'Loading...'}</p>
                </div>
            </PageContainer>
        );
    }

    if (!authorizedEmails.includes(userDetails.email)) {
        return (
            <PageContainer>
                <div className="flex flex-col items-center justify-center h-[50vh] text-white">
                    <h1 className="text-4xl font-bold mb-4">404 - Page Not Found (Access Denied)</h1>
                    <p>The page you are looking for does not exist for user: {userDetails.email}</p>
                </div>
            </PageContainer>
        );
    }

    return (
        <PageContainer>
            <div className="flex flex-col items-center justify-center p-8 text-white w-full max-w-5xl mx-auto">
                <div className="text-center mb-8 w-full">
                    <div className="flex items-center justify-center gap-4 mb-2">
                        <h1 className="text-4xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-purple-500">
                            Script Analysis Lab V3
                        </h1>
                        <label className="flex items-center gap-2 cursor-pointer select-none" title={extensionEnabled ? 'Extension active' : 'Extension paused'}>
                            <span className="text-sm text-gray-400">{extensionEnabled ? 'ON' : 'OFF'}</span>
                            <div
                                role="switch"
                                aria-checked={extensionEnabled}
                                tabIndex={0}
                                onClick={() => setExtensionEnabled(v => !v)}
                                onKeyDown={(e) => e.key === 'Enter' && setExtensionEnabled(v => !v)}
                                className={`relative w-12 h-6 rounded-full transition-colors cursor-pointer ${extensionEnabled ? 'bg-blue-600' : 'bg-gray-600'}`}
                            >
                                <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-all duration-200 ${extensionEnabled ? 'left-[28px]' : 'left-1'}`} />
                            </div>
                        </label>
                    </div>
                    <p className="text-gray-400 max-w-2xl mx-auto">
                        Extract text from PDF documents. {!extensionEnabled && <span className="text-amber-400">(Extension paused)</span>}
                    </p>
                </div>
                
                <div className="bg-gray-800 p-8 rounded-lg shadow-lg w-full space-y-8">
                    {/* Upload Section */}
                    <div className="border border-gray-600 p-6 rounded-lg">
                        <h2 className="text-xl font-semibold mb-4">Upload PDF Document</h2>
                        <div className="flex flex-col sm:flex-row gap-4 items-center">
                            <input 
                                type="file" 
                                accept=".pdf"
                                onChange={handleFileChange}
                                className="block w-full text-sm text-gray-400
                                  file:mr-4 file:py-2 file:px-4
                                  file:rounded-full file:border-0
                                  file:text-sm file:font-semibold
                                  file:bg-blue-600 file:text-white
                                  hover:file:bg-blue-700
                                "
                            />
                            <button 
                                onClick={handleUpload}
                                disabled={!extensionEnabled || !file || isLoading || isSummarizing}
                                className={`px-6 py-2 rounded font-bold transition-colors whitespace-nowrap ${
                                    extensionEnabled && file && !isLoading && !isSummarizing ? 'bg-purple-600 hover:bg-purple-700 text-white' : 'bg-gray-600 text-gray-400 cursor-not-allowed'
                                }`}
                            >
                                {isLoading ? 'Extracting Text...' : isSummarizing ? 'Generating Summary...' : 'Generate Summary'}
                            </button>
                        </div>
                        {error && (
                            <div className="mt-4 p-4 bg-red-900/50 border border-red-500 rounded text-red-200">
                                <strong>Error: </strong>{error}
                            </div>
                        )}
                        {/* Dev shortcut: skip chunk LLMs, run final synopsis only */}
                        <div className="mt-4 pt-4 border-t border-gray-700">
                            <button
                                onClick={handleLongSynopsisTest}
                                disabled={!extensionEnabled || isLoading || isSummarizing}
                                className={`px-5 py-2 rounded font-bold transition-colors text-sm ${
                                    extensionEnabled && !isLoading && !isSummarizing
                                        ? 'bg-amber-600 hover:bg-amber-500 text-white'
                                        : 'bg-gray-600 text-gray-400 cursor-not-allowed'
                                }`}
                            >
                                {isSummarizing ? 'Running...' : 'Long Synopsis Test'}
                            </button>
                            <p className="text-xs text-gray-500 mt-1">
                                Skips chunk LLMs — loads pre-filled BOLLYWOOD chunks and runs only the final synopsis call.
                            </p>
                        </div>
                    </div>

                    {/* Output Section */}
                    {extractedText ? (
                        <div className="space-y-4">
                            <h3 className="text-lg font-bold text-gray-300">Extracted Text:</h3>
                            <div className="bg-gray-900 p-4 rounded-lg overflow-y-auto whitespace-pre-wrap font-mono text-xs text-gray-400 h-48 max-h-48 border border-gray-700">
                                {extractedText}
                            </div>
                        </div>
                    ) : (
                        <div className="border border-gray-600 p-12 rounded-lg text-center text-gray-400 italic">
                            Upload a PDF to view extracted text and generate a summary.
                        </div>
                    )}

                    {/* Summary Section */}
                    {extractedText && (
                        <div className="border border-gray-600 p-6 rounded-lg space-y-4">
                            <h2 className="text-xl font-semibold">AI Summary (GPT-5 Mini Split)</h2>
                            
                            {summaryError && (
                                <div className="p-4 bg-red-950/80 border border-red-600 rounded-lg shadow-xl text-red-200 overflow-x-auto">
                                    <div className="font-bold text-red-400 mb-2 font-sans text-sm pb-2 border-b border-red-900/50">
                                        Pipeline Execution Error
                                    </div>
                                    <div className="whitespace-pre-wrap font-mono text-xs leading-relaxed">
                                        {summaryError}
                                    </div>
                                </div>
                            )}

                                    <div className="space-y-6 mt-6">
                                        <div className="flex flex-col sm:flex-row justify-between items-end sm:items-center gap-4">
                                            <h3 className="text-xl font-bold text-gray-200">
                                                Observability Log ({chunks.filter(c => c.status === 'COMPLETED').length} / {totalChunks})
                                            </h3>
                                            {chunks.length > 0 && (
                                                <div className="flex flex-wrap gap-2">
                                                    <button onClick={handleCopyAll} className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 rounded text-xs font-semibold transition-colors text-white">
                                                        Copy All Logs & Synopsis
                                                    </button>
                                                    <button onClick={handleCopyChunks} className="px-3 py-1.5 bg-gray-700 hover:bg-gray-600 rounded border border-gray-500 text-xs font-semibold transition-colors text-white">
                                                        Copy All Chunks
                                                    </button>
                                                    {finalSynopsis && (
                                                        <button onClick={handleCopySynopsis} className="px-3 py-1.5 bg-purple-600 hover:bg-purple-700 rounded text-xs font-semibold transition-colors text-white">
                                                            Copy Synopsis Only
                                                        </button>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                        <div className="flex flex-col gap-6">
                                            {chunks.map((chunk, idx) => (
                                                <div key={idx} className="bg-gray-900 rounded-lg border border-gray-700 shadow-xl overflow-hidden flex flex-col w-full">
                                                    
                                                    {/* Chunk Header */}
                                                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center p-4 bg-gray-800 border-b border-gray-700">
                                                        <div className="flex items-center gap-4 mb-2 sm:mb-0">
                                                            <h3 className="font-bold text-lg text-white">Chunk {chunk.sequence_number + 1}</h3>
                                                            <span className={`text-xs px-3 py-1 rounded-full font-bold uppercase tracking-wider ${chunk.status === 'COMPLETED' ? 'bg-green-900 text-green-300 border border-green-700' : chunk.status === 'FAILED' ? 'bg-red-900 text-red-300 border border-red-700' : 'bg-yellow-900 text-yellow-300 border border-yellow-700 animate-pulse'}`}>
                                                                {chunk.status}
                                                            </span>
                                                        </div>
                                                        <div className="flex items-center gap-3">
                                                            {chunk.text_result && (
                                                                <span className="text-xs bg-gray-700 text-gray-300 px-3 py-1 rounded border border-gray-600 font-mono">
                                                                    {(chunk.text_result.trim().split(/\s+/).filter(Boolean).length).toLocaleString()} words
                                                                </span>
                                                            )}
                                                            {chunk.tokens > 0 && (
                                                                <span className="text-xs bg-gray-700 text-gray-300 px-3 py-1 rounded border border-gray-600">
                                                                    <span className="font-mono">{chunk.tokens.toLocaleString()}</span> Tokens
                                                                </span>
                                                            )}
                                                            {chunk.cost > 0 && (
                                                                <span className="text-xs bg-blue-900/50 text-blue-300 px-3 py-1 rounded border border-blue-800">
                                                                    $<span className="font-mono">{chunk.cost.toFixed(4)}</span>
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>

                                                    {/* View Controls & Content */}
                                                    <div className="p-0">
                                                        <ChunkViewer chunk={chunk} />
                                                    </div>

                                                </div>
                                            ))}
                                        </div>
                                    </div>

                            {finalSynopsis && (
                                <div className="space-y-4 mt-8">
                                    <div className="flex items-center gap-4">
                                        <h3 className="text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-500">
                                            Final Long Synopsis (GPT-5.1)
                                        </h3>
                                        <span className="text-sm bg-gray-700 text-gray-300 px-3 py-1 rounded border border-gray-600 font-mono">
                                            {finalSynopsis.trim().split(/\s+/).length} words
                                        </span>
                                    </div>
                                    <div className="bg-gray-900 p-8 rounded-lg shadow-xl border border-gray-600">
                                        <div className="whitespace-pre-wrap font-serif text-base text-gray-200 leading-relaxed">
                                            {finalSynopsis}
                                        </div>
                                    </div>
                                </div>
                            )}
                            
                            {/* Final Synopsis Status (mirrors per-chunk status UX) */}
                            {jobId && (
                                <div className="mt-8">
                                    <div className="bg-gray-900 rounded-lg border border-gray-700 shadow-xl overflow-hidden flex flex-col w-full">
                                        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center p-4 bg-gray-800 border-b border-gray-700">
                                            <div className="flex items-center gap-4 mb-2 sm:mb-0">
                                                <h3 className="font-bold text-lg text-white">Final Synopsis</h3>
                                                {finalSynopsisStatus && (
                                                    <span
                                                        className={`text-xs px-3 py-1 rounded-full font-bold uppercase tracking-wider ${
                                                            finalSynopsisStatus === 'COMPLETED'
                                                                ? 'bg-green-900 text-green-300 border border-green-700'
                                                                : finalSynopsisStatus === 'FAILED'
                                                                    ? 'bg-red-900 text-red-300 border border-red-700'
                                                                    : finalSynopsisStatus === 'PROCESSING'
                                                                        ? 'bg-yellow-900 text-yellow-300 border border-yellow-700 animate-pulse'
                                                                        : 'bg-gray-800 text-gray-300 border border-gray-700'
                                                        }`}
                                                    >
                                                        {finalSynopsisStatus}
                                                    </span>
                                                )}
                                            </div>
                                            {(finalSynopsisBody || finalSynopsis) && (
                                                <div className="flex items-center gap-3">
                                                    <span className="text-sm bg-gray-700 text-gray-300 px-3 py-1 rounded border border-gray-600 font-mono">
                                                        {(finalSynopsisBody || finalSynopsis).trim().split(/\s+/).length} words
                                                    </span>
                                                </div>
                                            )}
                                        </div>

                                        <div className="p-6 bg-gray-950">
                                            {finalSynopsisNote && (
                                                <div className="mb-4 p-3 rounded border border-amber-700/50 bg-amber-950/30 text-amber-200 text-sm font-mono">
                                                    {finalSynopsisNote}
                                                </div>
                                            )}
                                            {finalSynopsisStatus === 'COMPLETED' && (finalSynopsisBody || finalSynopsis) ? (
                                                <div className="whitespace-pre-wrap font-serif text-base text-gray-200 leading-relaxed">
                                                    {finalSynopsisBody || finalSynopsis}
                                                </div>
                                            ) : finalSynopsisStatus === 'FAILED' ? (
                                                <div className="whitespace-pre-wrap font-mono text-xs text-red-300 bg-red-950/30 p-4 rounded border border-red-900/50">
                                                    {summaryError || 'Final synopsis failed to generate.'}
                                                </div>
                                            ) : finalSynopsisStatus === 'PROCESSING' ? (
                                                <div className="text-gray-400 italic">
                                                    Chunks are finished. Generating the final long synopsis now...
                                                </div>
                                            ) : (
                                                <div className="text-gray-500 italic">
                                                    Waiting for chunk summaries to finish before generating the final long synopsis...
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* ── Meta passes run after long synopsis is done; derive their loading state ── */}
                            {(() => {
                                // Show downstream loading states as soon as the long synopsis exists and job is live.
                                // This ensures sections appear even if the poll catches COMPLETED in one shot.
                                const jobRunning = jobStatus === 'PROCESSING' || jobStatus === 'PENDING';
                                const jobDone = jobStatus === 'COMPLETED' || jobStatus === 'FAILED';
                                const longSynopsisReady = !!(finalSynopsis);
                                const shortLoading = jobRunning && longSynopsisReady && !shortSynopsis;
                                const metaLoading = jobRunning && longSynopsisReady && !metaLogline && !metaCharacters.length;
                                // Show a "failed" state when job is done but fields are still empty (backend error)
                                const shortFailed = jobDone && longSynopsisReady && !shortSynopsis;
                                const metaFailed = jobDone && longSynopsisReady && !metaLogline && !metaCharacters.length;

                                const FailedPulse = ({ label }) => (
                                    <div className="flex items-center gap-3 p-4 bg-red-950/30 rounded-lg border border-red-800/50">
                                        <div className="w-2 h-2 rounded-full bg-red-500" />
                                        <span className="text-sm text-red-400 italic">{label} failed to generate. Check error log.</span>
                                    </div>
                                );

                                const LoadingPulse = ({ label }) => (
                                    <div className="flex items-center gap-3 p-4 bg-gray-900/60 rounded-lg border border-gray-700 animate-pulse">
                                        <div className="w-2 h-2 rounded-full bg-yellow-400 animate-ping" />
                                        <span className="text-sm text-gray-400 italic">{label} generating...</span>
                                    </div>
                                );

                                return (
                                    <>
                                        {/* ── Short Synopsis ── */}
                                        {(shortSynopsis || shortLoading || shortFailed) && (
                                            <div className="space-y-3 mt-8">
                                                <div className="flex items-center gap-3">
                                                    <h3 className="text-lg font-bold text-transparent bg-clip-text bg-gradient-to-r from-teal-400 to-cyan-500">
                                                        Short Synopsis
                                                    </h3>
                                                    {shortLoading && <span className="text-xs px-2 py-0.5 rounded-full bg-yellow-900 text-yellow-300 border border-yellow-700 animate-pulse font-bold uppercase tracking-wider">Processing</span>}
                                                    {shortFailed && <span className="text-xs px-2 py-0.5 rounded-full bg-red-900 text-red-300 border border-red-700 font-bold uppercase tracking-wider">Failed</span>}
                                                    {shortSynopsis && <span className="text-xs px-2 py-0.5 rounded-full bg-green-900 text-green-300 border border-green-700 font-bold uppercase tracking-wider">Done</span>}
                                                </div>
                                                {shortLoading ? <LoadingPulse label="Short synopsis" />
                                                    : shortFailed ? <FailedPulse label="Short synopsis" />
                                                    : <div className="bg-gray-900 p-6 rounded-lg border border-gray-700 whitespace-pre-wrap font-serif text-sm text-gray-200 leading-relaxed">{shortSynopsis}</div>
                                                }
                                            </div>
                                        )}

                                        {/* ── Logline ── */}
                                        {(metaLogline || metaLoading || metaFailed) && (
                                            <div className="space-y-3 mt-8">
                                                <div className="flex items-center gap-3">
                                                    <h3 className="text-lg font-bold text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-orange-400">
                                                        Logline
                                                    </h3>
                                                    {metaLoading && <span className="text-xs px-2 py-0.5 rounded-full bg-yellow-900 text-yellow-300 border border-yellow-700 animate-pulse font-bold uppercase tracking-wider">Processing</span>}
                                                    {metaFailed && <span className="text-xs px-2 py-0.5 rounded-full bg-red-900 text-red-300 border border-red-700 font-bold uppercase tracking-wider">Failed</span>}
                                                    {metaLogline && <span className="text-xs px-2 py-0.5 rounded-full bg-green-900 text-green-300 border border-green-700 font-bold uppercase tracking-wider">Done</span>}
                                                </div>
                                                {metaLoading ? <LoadingPulse label="Logline" />
                                                    : metaFailed ? <FailedPulse label="Logline" />
                                                    : <div className="bg-gray-900 p-6 rounded-lg border border-yellow-800/40 text-gray-200 font-serif text-base italic leading-relaxed">{metaLogline}</div>
                                                }
                                            </div>
                                        )}

                                        {/* ── Adaptability Notes ── */}
                                        {(metaAdaptability || metaLoading || metaFailed) && (
                                            <div className="space-y-3 mt-8">
                                                <div className="flex items-center gap-3">
                                                    <h3 className="text-lg font-bold text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-rose-400">
                                                        Adaptability Notes
                                                    </h3>
                                                    {metaLoading && <span className="text-xs px-2 py-0.5 rounded-full bg-yellow-900 text-yellow-300 border border-yellow-700 animate-pulse font-bold uppercase tracking-wider">Processing</span>}
                                                    {metaFailed && <span className="text-xs px-2 py-0.5 rounded-full bg-red-900 text-red-300 border border-red-700 font-bold uppercase tracking-wider">Failed</span>}
                                                    {metaAdaptability && <span className="text-xs px-2 py-0.5 rounded-full bg-green-900 text-green-300 border border-green-700 font-bold uppercase tracking-wider">Done</span>}
                                                </div>
                                                {metaLoading ? <LoadingPulse label="Adaptability notes" />
                                                    : metaFailed ? <FailedPulse label="Adaptability notes" />
                                                    : <div className="bg-gray-900 p-6 rounded-lg border border-gray-700 whitespace-pre-wrap text-sm text-gray-200 leading-relaxed">{metaAdaptability}</div>
                                                }
                                            </div>
                                        )}

                                        {/* ── Key Demographics ── */}
                                        {(metaDemographics || metaLoading || metaFailed) && (
                                            <div className="space-y-3 mt-8">
                                                <div className="flex items-center gap-3">
                                                    <h3 className="text-lg font-bold text-transparent bg-clip-text bg-gradient-to-r from-pink-400 to-fuchsia-500">
                                                        Key Demographics
                                                    </h3>
                                                    {metaLoading && <span className="text-xs px-2 py-0.5 rounded-full bg-yellow-900 text-yellow-300 border border-yellow-700 animate-pulse font-bold uppercase tracking-wider">Processing</span>}
                                                    {metaFailed && <span className="text-xs px-2 py-0.5 rounded-full bg-red-900 text-red-300 border border-red-700 font-bold uppercase tracking-wider">Failed</span>}
                                                    {metaDemographics && <span className="text-xs px-2 py-0.5 rounded-full bg-green-900 text-green-300 border border-green-700 font-bold uppercase tracking-wider">Done</span>}
                                                </div>
                                                {metaLoading ? <LoadingPulse label="Key demographics" />
                                                    : metaFailed ? <FailedPulse label="Key demographics" />
                                                    : metaDemographics && Object.keys(metaDemographics).length > 0 && (
                                                        <div className="bg-gray-900 rounded-lg border border-gray-700 overflow-hidden">
                                                            <table className="w-full text-sm">
                                                                <tbody className="divide-y divide-gray-800">
                                                                    {[
                                                                        ['Age Range', metaDemographics.age_range],
                                                                        ['Gender', metaDemographics.gender],
                                                                        ['Socioeconomic Status', metaDemographics.socioeconomic_status],
                                                                        ['Interests', metaDemographics.interests],
                                                                        ['Viewing Habits', metaDemographics.viewing_habits],
                                                                    ].filter(([, v]) => v).map(([label, value]) => (
                                                                        <tr key={label} className="hover:bg-gray-800/40 transition-colors">
                                                                            <td className="px-5 py-3 text-gray-400 font-medium w-48">{label}</td>
                                                                            <td className="px-5 py-3 text-gray-200">{value}</td>
                                                                        </tr>
                                                                    ))}
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    )
                                                }
                                            </div>
                                        )}

                                        {/* ── Comparable Titles ── */}
                                        {(metaComps || metaLoading || metaFailed) && (
                                            <div className="space-y-3 mt-8">
                                                <div className="flex items-center gap-3">
                                                    <h3 className="text-lg font-bold text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 to-blue-400">
                                                        Comparable Titles
                                                    </h3>
                                                    {metaLoading && <span className="text-xs px-2 py-0.5 rounded-full bg-yellow-900 text-yellow-300 border border-yellow-700 animate-pulse font-bold uppercase tracking-wider">Processing</span>}
                                                    {metaFailed && <span className="text-xs px-2 py-0.5 rounded-full bg-red-900 text-red-300 border border-red-700 font-bold uppercase tracking-wider">Failed</span>}
                                                    {metaComps && <span className="text-xs px-2 py-0.5 rounded-full bg-green-900 text-green-300 border border-green-700 font-bold uppercase tracking-wider">Done</span>}
                                                </div>
                                                {metaLoading ? <LoadingPulse label="Comparable titles" />
                                                    : metaFailed ? <FailedPulse label="Comparable titles" />
                                                    : metaComps && Object.keys(metaComps).length > 0 && (
                                                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                                            {[
                                                                ['Films', metaComps.films],
                                                                ['Series', metaComps.series],
                                                                ['Books', metaComps.books],
                                                            ].map(([label, items]) => (
                                                                <div key={label} className="bg-gray-900 rounded-lg border border-gray-700 p-4">
                                                                    <div className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">{label}</div>
                                                                    {Array.isArray(items) && items.length > 0 ? (
                                                                        <ul className="space-y-1">
                                                                            {items.map((title, i) => (
                                                                                <li key={i} className="text-gray-200 text-sm flex items-start gap-2">
                                                                                    <span className="text-indigo-500 mt-0.5">▸</span>
                                                                                    {title}
                                                                                </li>
                                                                            ))}
                                                                        </ul>
                                                                    ) : (
                                                                        <p className="text-gray-500 italic text-sm">None listed</p>
                                                                    )}
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )
                                                }
                                            </div>
                                        )}

                                        {/* ── Character Breakdowns ── */}
                                        {(metaCharacters.length > 0 || metaLoading || metaFailed) && (
                                            <div className="space-y-3 mt-8">
                                                <div className="flex items-center gap-3">
                                                    <h3 className="text-lg font-bold text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-green-400">
                                                        Character Breakdowns{metaCharacters.length > 0 ? ` (${metaCharacters.length})` : ''}
                                                    </h3>
                                                    {metaLoading && <span className="text-xs px-2 py-0.5 rounded-full bg-yellow-900 text-yellow-300 border border-yellow-700 animate-pulse font-bold uppercase tracking-wider">Processing</span>}
                                                    {metaFailed && <span className="text-xs px-2 py-0.5 rounded-full bg-red-900 text-red-300 border border-red-700 font-bold uppercase tracking-wider">Failed</span>}
                                                    {metaCharacters.length > 0 && <span className="text-xs px-2 py-0.5 rounded-full bg-green-900 text-green-300 border border-green-700 font-bold uppercase tracking-wider">Done</span>}
                                                </div>
                                                {metaLoading ? <LoadingPulse label="Character breakdowns" />
                                                    : metaFailed ? <FailedPulse label="Character breakdowns" />
                                                    : (
                                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                                            {metaCharacters.map((char, i) => (
                                                                <div key={i} className="bg-gray-900 rounded-lg border border-gray-700 p-5 flex flex-col gap-2">
                                                                    <div className="flex items-center gap-3 flex-wrap">
                                                                        <span className="font-bold text-white text-base">{char.name || `Character ${i + 1}`}</span>
                                                                        {char.gender && (
                                                                            <span className="text-xs px-2 py-0.5 rounded bg-gray-700 text-gray-300 border border-gray-600">{char.gender}</span>
                                                                        )}
                                                                        {char.age_range && (
                                                                            <span className="text-xs px-2 py-0.5 rounded bg-gray-700 text-gray-300 border border-gray-600">{char.age_range}</span>
                                                                        )}
                                                                    </div>
                                                                    {char.description && (
                                                                        <p className="text-sm text-gray-300 leading-relaxed">{char.description}</p>
                                                                    )}
                                                                    {char.character_arc && (
                                                                        <div className="mt-1">
                                                                            <span className="text-xs font-semibold text-emerald-400 uppercase tracking-wider">Arc</span>
                                                                            <p className="text-sm text-gray-400 leading-relaxed mt-1">{char.character_arc}</p>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )
                                                }
                                            </div>
                                        )}
                                    </>
                                );
                            })()}

                            {/* ── Run Summary Observability Panel ── */}
                            {jobId && (chunks.length > 0 || finalMeta) && (
                                (() => {
                                    // Parse model/attempts out of the prompt_input header that tasks.py prepends
                                    const parseChunkMeta = (chunk) => {
                                        const pi = chunk.prompt_input || '';
                                        const model = pi.match(/^MODEL_USED:\s*(.+)$/m)?.[1]?.trim() || '—';
                                        const attMatch = pi.match(/across (\d+) attempt/);
                                        const attempts = attMatch ? parseInt(attMatch[1]) : 1;
                                        return { model, attempts };
                                    };

                                    const fmt$ = (n) => `$${(n || 0).toFixed(4)}`;
                                    const fmtTok = (n) => (n || 0).toLocaleString();

                                    const chunkTotalTokens = chunks.reduce((s, c) => s + (c.tokens || 0), 0);
                                    const chunkTotalCost = chunks.reduce((s, c) => s + (c.cost || 0), 0);
                                    const grandTokens = chunkTotalTokens + (finalMeta?.tokens || 0);
                                    const grandCost = chunkTotalCost + (finalMeta?.cost || 0);

                                    const outcomeColor = (outcome) => {
                                        if (!outcome || outcome === 'success') return 'text-green-400';
                                        if (outcome === 'best_effort') return 'text-amber-400';
                                        if (['refusal', 'content_filter', 'empty_response'].includes(outcome)) return 'text-red-400';
                                        return 'text-yellow-400';
                                    };

                                    return (
                                        <div className="mt-6">
                                            <div className="bg-gray-900 rounded-lg border border-gray-700 shadow-xl overflow-hidden">
                                                <div className="px-4 py-3 bg-gray-800 border-b border-gray-700 flex items-center gap-3">
                                                    <h3 className="font-bold text-white text-sm uppercase tracking-wider">Run Summary</h3>
                                                    <span className="text-xs text-gray-400 font-mono">{fmtTok(grandTokens)} tokens · {fmt$(grandCost)} total</span>
                                                </div>

                                                <div className="overflow-x-auto">
                                                    <table className="w-full text-xs font-mono">
                                                        <thead>
                                                            <tr className="text-gray-400 border-b border-gray-700 bg-gray-850">
                                                                <th className="text-left px-4 py-2">Step</th>
                                                                <th className="text-left px-4 py-2">Status</th>
                                                                <th className="text-left px-4 py-2">Final Model</th>
                                                                <th className="text-right px-4 py-2">Attempts</th>
                                                                <th className="text-right px-4 py-2">Tokens</th>
                                                                <th className="text-right px-4 py-2">Cost (incl. retries)</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody className="divide-y divide-gray-800">
                                                            {chunks.map((chunk, i) => {
                                                                const { model, attempts } = parseChunkMeta(chunk);
                                                                const hasRetries = attempts > 1 || (chunk.attempt_log || []).length > 1;
                                                                return (
                                                                    <React.Fragment key={i}>
                                                                        <tr className="hover:bg-gray-800/50 transition-colors">
                                                                            <td className="px-4 py-2 text-gray-300">Chunk {i + 1}</td>
                                                                            <td className="px-4 py-2">
                                                                                <span className={`px-2 py-0.5 rounded text-xs font-bold ${
                                                                                    chunk.status === 'COMPLETED' ? 'bg-green-900/50 text-green-300' :
                                                                                    chunk.status === 'FAILED' ? 'bg-red-900/50 text-red-300' :
                                                                                    chunk.status === 'PROCESSING' ? 'bg-yellow-900/50 text-yellow-300' :
                                                                                    'bg-gray-700 text-gray-400'
                                                                                }`}>{chunk.status}</span>
                                                                            </td>
                                                                            <td className="px-4 py-2 text-gray-300">{model}</td>
                                                                            <td className="px-4 py-2 text-right">
                                                                                {hasRetries
                                                                                    ? <span className="text-amber-400 font-bold">{attempts}</span>
                                                                                    : <span className="text-gray-400">{attempts}</span>
                                                                                }
                                                                            </td>
                                                                            <td className="px-4 py-2 text-right text-gray-300">{fmtTok(chunk.tokens)}</td>
                                                                            <td className="px-4 py-2 text-right text-gray-300">{fmt$(chunk.cost)}</td>
                                                                        </tr>
                                                                        {/* Per-attempt breakdown rows */}
                                                                        {(chunk.attempt_log || []).length > 1 && (chunk.attempt_log || []).map((att, ai) => {
                                                                            const isUsed = att.outcome === 'success' || att.outcome === 'best_effort';
                                                                            return (
                                                                                <tr key={`${i}-att-${ai}`} className={`border-l-2 ${isUsed ? 'bg-green-950/40 border-green-600' : 'bg-gray-950 border-amber-700/40'}`}>
                                                                                    <td className={`pl-8 pr-4 py-1 ${isUsed ? 'text-green-300 font-bold' : 'text-gray-500'}`}>
                                                                                        ↳ attempt {att.attempt}{isUsed ? ' ✓ used' : ''}
                                                                                    </td>
                                                                                    <td className="px-4 py-1">
                                                                                        <span className={`text-xs ${outcomeColor(att.outcome)}`}>{att.outcome}</span>
                                                                                    </td>
                                                                                    <td className={`px-4 py-1 ${isUsed ? 'text-green-300' : 'text-gray-500'}`}>{att.model}</td>
                                                                                    <td className="px-4 py-1 text-right text-gray-500">—</td>
                                                                                    <td className={`px-4 py-1 text-right ${isUsed ? 'text-green-300' : 'text-gray-500'}`}>{fmtTok(att.tokens)}</td>
                                                                                    <td className={`px-4 py-1 text-right ${isUsed ? 'text-green-300' : 'text-gray-500'}`}>{fmt$(att.cost)}</td>
                                                                                </tr>
                                                                            );
                                                                        })}
                                                                    </React.Fragment>
                                                                );
                                                            })}

                                                            {/* Final synopsis row */}
                                                            {finalMeta && (
                                                                <React.Fragment>
                                                                    <tr className="hover:bg-gray-800/50 transition-colors border-t border-gray-600">
                                                                        <td className="px-4 py-2 text-blue-300 font-bold">Final Synopsis</td>
                                                                        <td className="px-4 py-2">
                                                                            <span className={`px-2 py-0.5 rounded text-xs font-bold ${
                                                                                finalSynopsisStatus === 'COMPLETED' ? 'bg-green-900/50 text-green-300' :
                                                                                finalSynopsisStatus === 'FAILED' ? 'bg-red-900/50 text-red-300' :
                                                                                'bg-yellow-900/50 text-yellow-300'
                                                                            }`}>{finalSynopsisStatus || 'PENDING'}</span>
                                                                        </td>
                                                                        <td className="px-4 py-2 text-gray-300">{finalMeta.model || '—'}</td>
                                                                        <td className="px-4 py-2 text-right">
                                                                            {(finalMeta.attempts || 0) > 1
                                                                                ? <span className="text-amber-400 font-bold">{finalMeta.attempts}</span>
                                                                                : <span className="text-gray-400">{finalMeta.attempts || '—'}</span>
                                                                            }
                                                                        </td>
                                                                        <td className="px-4 py-2 text-right text-gray-300">{fmtTok(finalMeta.tokens)}</td>
                                                                        <td className="px-4 py-2 text-right text-gray-300">{fmt$(finalMeta.cost)}</td>
                                                                    </tr>
                                                                    {/* Final synopsis per-attempt breakdown */}
                                                                    {(finalMeta.attempt_log || []).length > 1 && (finalMeta.attempt_log || []).map((att, ai) => {
                                                                        const isUsed = att.outcome === 'success' || att.outcome === 'best_effort';
                                                                        return (
                                                                            <tr key={`final-att-${ai}`} className={`border-l-2 ${isUsed ? 'bg-green-950/40 border-green-600' : 'bg-gray-950 border-blue-700/40'}`}>
                                                                                <td className={`pl-8 pr-4 py-1 ${isUsed ? 'text-green-300 font-bold' : 'text-gray-500'}`}>
                                                                                    ↳ attempt {att.attempt}{isUsed ? ' ✓ used' : ''}
                                                                                </td>
                                                                                <td className="px-4 py-1">
                                                                                    <span className={`text-xs ${outcomeColor(att.outcome)}`}>{att.outcome}</span>
                                                                                </td>
                                                                                <td className={`px-4 py-1 ${isUsed ? 'text-green-300' : 'text-gray-500'}`}>{att.model}</td>
                                                                                <td className="px-4 py-1 text-right text-gray-500">—</td>
                                                                                <td className={`px-4 py-1 text-right ${isUsed ? 'text-green-300' : 'text-gray-500'}`}>{fmtTok(att.tokens)}</td>
                                                                                <td className={`px-4 py-1 text-right ${isUsed ? 'text-green-300' : 'text-gray-500'}`}>{fmt$(att.cost)}</td>
                                                                            </tr>
                                                                        );
                                                                    })}
                                                                </React.Fragment>
                                                            )}

                                                            {/* Grand total row */}
                                                            <tr className="bg-gray-800 border-t-2 border-gray-600 font-bold">
                                                                <td className="px-4 py-2 text-white" colSpan={4}>TOTAL (all chunks + final synopsis, incl. retries)</td>
                                                                <td className="px-4 py-2 text-right text-white">{fmtTok(grandTokens)}</td>
                                                                <td className="px-4 py-2 text-right text-white">{fmt$(grandCost)}</td>
                                                            </tr>
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })()
                            )}
                        </div>
                    )}
                </div>
            </div>
        </PageContainer>
    );
};

export default LabV3;
